import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import PricePanel from "src/main/Billing/Items/EditInstallmentStay/shared/PricePanel"
import {
  centsToDollars,
  dollarsToCents,
  toTaxPercent,
  toTaxRate,
} from "src/main/Billing/Items/helpers"
import { validatePrecision } from "src/main/Billing/Items/validators"
import { SharedBillingContext } from "src/main/Billing/SharedBillingContext"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import Divider from "src/components/Divider"
import Form from "src/components/Form"
import Modal from "src/components/Modal"
import ReloadableWidget from "src/components/ReloadableWidget"
import TaxRateDisplayInput from "src/components/TaxRateDisplayInput"
import { DangerousDeprecatedOldReactAdapterComponent } from "src/components/__dangerous__"

import { queryProducts } from "src/api/Billing/Items"
import { queryInvoices } from "src/api/Billing/Payments"
import { cancelContract } from "src/api/Contracts"
import { cancelReservation } from "src/api/Reservation"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import DueDateField from "../DueDateField"
import RefundOptionsDropdown from "../RefundOptionsDropdown"
import { useGetRefundablePaymentMethods } from "../hooks"
import {
  calculateCancellationFeeTotal,
  calculateItemizedCancellationFeeTotal,
} from "./helpers"

export const getMultiplePaymentText = (contractQuoteId) =>
  `More than one payment method was used on this ${
    contractQuoteId ? "contract" : "reservation"
  }. To issue a refund, please remove billing products / services manually.`
export const getProcessingInvoicePaymentText = (contractQuoteId) =>
  `This ${
    contractQuoteId ? "contract" : "reservation"
  } cannot be refunded due to an invoice that is still processing.`
export const HAS_DISPUTES_TEXT =
  "Payment(s) are under dispute. To issue a refund, please remove billing products / services manually."
export const NOT_REMOVING_ITEMS_TEXT =
  "By unchecking this box, all paid and unpaid invoices will remain on the contract. Your customer may still receive reminders for upcoming payments if they have unpaid invoices. You can manually remove these items from the Items table if needed."
export const UNSETTLED_REFUND_INVOICE_TEXT =
  "A cancellation fee cannot be applied due to an unsettled refund invoice."

const CancelContractModal = ({
  reservationId,
  contractQuoteId,
  deprecatedButtonTargetId,
  buttonVariant = "secondary",
  hasDisputes = false,
  cancellationPolicyPath = null,
  cancellationEntitlement = null,
  useNewTaxExperience = false,
  billingContainerId: billingContainerIdProp = null,
}) => {
  const { billingContainerId: contextBillingContainerId } =
    useContext(SharedBillingContext) || {}
  const billingContainerId = billingContainerIdProp ?? contextBillingContainerId
  const { presentmentCurrency, marinaSlug, currencySymbol } = useMarina()

  const MULTIPLE_PAYMENT_TEXT = getMultiplePaymentText(contractQuoteId)
  const PROCESSING_INVOICE_TEXT =
    getProcessingInvoicePaymentText(contractQuoteId)
  const [isOpen, setIsOpen] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    setError,
    clearErrors,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      isRemovingProducts: true,
      cancellationNote: "",
      dueDate: new Date(),
    },
  })

  const [
    cancellationFeeAmount,
    cancellationFeeProductId,
    cancellationFeeTaxRate,
    isRemoving,
  ] = watch([
    "cancellationFeeAmount",
    "cancellationFeeProductId",
    "cancellationFeeTaxRate",
    "isRemovingProducts",
  ])

  // Fetching Payment Methods
  const {
    data: { isMultiplePaymentRequired, refundablePaymentMethods },
    isFetching: isLoadingRefundablePaymentMethods,
    isError: isErrorRefundablePaymentMethods,
  } = useGetRefundablePaymentMethods({
    billingContainerId,
    options: {
      onSuccess: ({ isMultiplePaymentRequired }) => {
        if (isMultiplePaymentRequired || hasDisputes) {
          setValue("isRemovingProducts", false)
        }
      },
      select: (data) => ({
        refundablePaymentMethods: data,
        isMultiplePaymentRequired: data.length > 1,
      }),
      onError: () => {
        setError("root.serverError", {
          message: "Something went wrong! Please contact support.",
        })
      },
      placeholderData: [],
      enabled: isOpen && Boolean(billingContainerId),
    },
  })

  // Fetching invoices
  const {
    isFetching: isLoadingInvoices,
    isError: isErrorInvoices,
    data: invoicesData,
  } = useQuery({
    queryKey: ["invoices", reservationId],
    queryFn: () => queryInvoices({ marinaSlug, reservationId }),
    placeholderData: [],
    enabled: isOpen,
  })

  // Fetch the marina's products that have a `cancellation_fees` availability
  const {
    isFetching: isLoadingCancellationFeeProducts,
    isError: isErrorCancellationFeeProducts,
    data: cancellationFeeProducts,
  } = useQuery({
    queryKey: ["cancellationFeeProducts", marinaSlug],
    queryFn: () =>
      queryProducts({
        page: 1,
        marinaSlug,
        availability: ["cancellation_fees"],
      }),
    placeholderData: [],
    enabled: isOpen,
  })

  const onModalClose = () => {
    setIsOpen(false)
  }

  const hasProcessingInvoice = invoicesData.some(
    (invoice) =>
      invoice.settled_at === null && invoice.display_status === "Processing"
  )
  const unsettledRefundInvoices = invoicesData.filter(
    (invoice) =>
      invoice.total_amount < 0 &&
      invoice.balance < 0 &&
      invoice.settled_at === null
  )
  const hasUnsettledRefundInvoice = unsettledRefundInvoices.length > 0
  const unsettledRefundTotalDue = unsettledRefundInvoices.reduce(
    (accumulator, invoice) => accumulator - invoice.balance,
    0
  )

  // processing payments as treated as settled with a 0 balance
  const customerPaymentNetAmount = invoicesData.reduce(
    (accumulator, invoice) =>
      accumulator + (invoice.total_amount - invoice.balance),
    0
  )
  const isTransientWithNoPayments =
    !contractQuoteId &&
    invoicesData.every(
      (invoice) =>
        invoice.settled_at === null && invoice.balance === invoice.total_amount
    )

  const cannotIssueRefund =
    isMultiplePaymentRequired || hasDisputes || hasProcessingInvoice
  const feeIsSupported =
    isRemoving &&
    !hasUnsettledRefundInvoice &&
    (isTransientWithNoPayments || customerPaymentNetAmount > 0)

  // handles unchecking the remove/refund checkbox when we detect
  // a processing invoice is present upon fetching invoice data,
  // whether the first time or on a re-fetch done on re-focus
  useEffect(() => {
    if (hasProcessingInvoice) {
      setValue("isRemovingProducts", false)
    }
  }, [hasProcessingInvoice, setValue])

  // handles resetting the cancellation fee inputs when we detect
  // that a fee is no longer supported due to:
  // - not removing items
  // - there being an unsettled refund invoice
  // - everything paid so far having been returned/refunded
  useEffect(() => {
    if (!feeIsSupported && cancellationFeeProductId) {
      setValue("cancellationFeeProductId", "")
      setValue("cancellationFeeAmount", 0)
      setValue("cancellationFeeTaxRate", 0)
    }
  }, [feeIsSupported, cancellationFeeProductId, setValue])

  const calculateEffectiveRefundTotal = () => {
    let refundValue = 0
    if (isRemoving && !!cancellationFeeAmount) {
      let feeTotal
      if (useNewTaxExperience) {
        const selectedProduct = cancellationFeeProducts.find(
          (product) => product.id === cancellationFeeProductId
        )
        feeTotal = calculateItemizedCancellationFeeTotal(
          cancellationFeeAmount,
          selectedProduct?.taxes
        )
      } else {
        feeTotal = calculateCancellationFeeTotal(
          cancellationFeeAmount,
          cancellationFeeTaxRate
        )
      }

      refundValue =
        customerPaymentNetAmount - feeTotal - unsettledRefundTotalDue
    } else if (isRemoving) {
      refundValue = customerPaymentNetAmount - unsettledRefundTotalDue
    }

    return refundValue
  }

  // validations
  const validateCancellationFeeAmount = (value) => {
    if (value <= 0) {
      return "Cancellation fee amount must be greater than 0."
    }

    const precisionError = validatePrecision(2)(value)
    if (precisionError) {
      return precisionError
    }

    let feeValue
    let hasTax = false
    if (useNewTaxExperience) {
      const selectedProduct = cancellationFeeProducts.find(
        (product) => product.id === cancellationFeeProductId
      )
      feeValue = calculateItemizedCancellationFeeTotal(
        value,
        selectedProduct?.taxes
      )
      hasTax = selectedProduct?.taxes?.length > 0
    } else {
      feeValue = calculateCancellationFeeTotal(value, cancellationFeeTaxRate)
      const taxRate = cancellationFeeTaxRate
        ? toTaxRate(cancellationFeeTaxRate)
        : 0
      hasTax = taxRate > 0
    }

    if (isTransientWithNoPayments) {
      const reservationTotal = invoicesData.reduce(
        (accumulator, invoice) => accumulator + invoice.total_amount,
        0
      )
      if (hasTax && feeValue > reservationTotal) {
        return "Cancellation fee with tax cannot exceed reservation total."
      } else if (feeValue > reservationTotal) {
        return "Cancellation fee amount cannot exceed reservation total."
      }
    } else {
      if (hasTax && feeValue > customerPaymentNetAmount) {
        return "Cancellation fee with tax cannot exceed amount paid by customer."
      } else if (feeValue > customerPaymentNetAmount) {
        return "Cancellation fee amount cannot exceed amount paid by customer."
      }
    }
  }

  const validateCancellationFeeTaxRate = (value) => {
    if (value < 0 || value > 100) {
      return "Cancellation fee tax must be between 0 and 100."
    }

    const precisionError = validatePrecision(4)(value)
    if (precisionError) {
      return precisionError
    }
  }

  const { mutate, isLoading, isSuccess } = useMutation({
    queryKey: ["cancel", reservationId],
    mutationFn: (data) =>
      contractQuoteId
        ? cancelContract(contractQuoteId, data, marinaSlug)
        : cancelReservation(reservationId, data, marinaSlug),
    onSuccess: ({ redirect_to: redirectTo }) =>
      window.location.assign(new URL(redirectTo, window.location.origin)),
    onError: () => {
      setError("root.serverError", {
        message:
          "There was a problem canceling this contract. Please try again or contact support.",
      })
    },
  })

  const handleCancellationFeeProductSelection = (productId) => {
    clearErrors(["cancellationFeeAmount", "cancellationFeeTaxRate"])
    setValue("cancellationFeeProductId", productId)

    const product = cancellationFeeProducts.find(
      (product) => product.id === productId
    )
    const cancellationFeeAmount = product?.price_per_unit ?? 0

    setValue("cancellationFeeAmount", centsToDollars(cancellationFeeAmount))

    if (!useNewTaxExperience) {
      const cancellationFeeTax = product?.tax_rate ?? 0
      setValue("cancellationFeeTaxRate", toTaxPercent(cancellationFeeTax))
    }
  }

  const onSubmit = ({ isRemovingProducts, cancellationNote, dueDate }) => {
    const formattedData = {
      cancellation_note: cancellationNote ? cancellationNote.trim() : null,
      return_product_sale_txns: isRemovingProducts,
      refund_due_date: isRemovingProducts ? dueDate : null,
      payment_method_id: cannotIssueRefund
        ? null
        : refundablePaymentMethods[0]?.payment_method_id,
    }

    if (feeIsSupported && cancellationFeeProductId) {
      formattedData.cancellation_fee_product_id = cancellationFeeProductId
      formattedData.cancellation_fee_amount = dollarsToCents(
        cancellationFeeAmount
      )

      if (useNewTaxExperience) {
        // Backend will use product's taxes for itemized calculation
        formattedData.cancellation_fee_tax_rate = null
      } else {
        formattedData.cancellation_fee_tax_rate = toTaxRate(
          cancellationFeeTaxRate
        )
      }
    }

    mutate(formattedData)
  }

  const handleFeeProductLinkClick = () => {
    if (cancellationFeeProductId) {
      // reset the product select so that when they return to the modal and
      // the products query refetches fresh data that they can re-select
      // the intended product and have the form populate with updated values
      handleCancellationFeeProductSelection("")
    }

    const destinationPath = cancellationFeeProductId
      ? `/manage/${marinaSlug}/items/${cancellationFeeProductId}/edit`
      : `/manage/${marinaSlug}/items/new?type=cancellationFee`
    window.open(destinationPath, "_blank", "noreferrer")
  }

  const renderBody = () => {
    const resourcesLoading =
      isLoadingRefundablePaymentMethods ||
      isLoadingInvoices ||
      isLoadingCancellationFeeProducts
    const hasResourceError =
      isErrorRefundablePaymentMethods ||
      isErrorInvoices ||
      isErrorCancellationFeeProducts

    return (
      <ReloadableWidget isLoading={resourcesLoading} isError={hasResourceError}>
        <div className="flex flex-col gap-4">
          {renderCannotIssueRefundBanner()}
          {renderUnsettledRefundBanner()}
          {renderCancellationPolicy()}
          {renderPaymentAndRefundAmountsDisplay()}
        </div>
        <Divider />
        <div className="flex flex-col gap-4">
          {renderCancellationFeeProductSelect()}
          {renderCancellationFeeInputs()}
          <div>
            <Form.Label htmlFor="cancellation-note">
              Cancellation Note
            </Form.Label>
            <Form.TextField
              id="cancellation-note"
              {...register("cancellationNote")}
            />
          </div>
        </div>
        <Divider />
        <div className="flex flex-col gap-4">
          {renderRemoveItemsCheckbox()}
          {renderNotRemovingItemsHelpText()}
          {renderRefundInputs()}
        </div>
      </ReloadableWidget>
    )
  }

  const renderCancelBtn = () => {
    const btn = (
      <Button variant={buttonVariant} onClick={() => setIsOpen(true)}>
        Cancel {contractQuoteId ? "Contract" : "Reservation"}
      </Button>
    )

    return deprecatedButtonTargetId ? (
      <DangerousDeprecatedOldReactAdapterComponent
        targetId={deprecatedButtonTargetId}
        weAcknowledgeThisTechniqueIsDangerous
      >
        {btn}
      </DangerousDeprecatedOldReactAdapterComponent>
    ) : (
      btn
    )
  }

  const renderCancellationPolicy = () => {
    if (contractQuoteId || !cancellationPolicyPath) {
      return null
    }

    return (
      <div className="mb-4">
        You can view the terms of the{" "}
        <a
          href={cancellationPolicyPath}
          className="text-link font-semibold no-underline"
          target="_blank"
          rel="noreferrer"
        >
          cancellation agreement
        </a>{" "}
        here. Apply an optional cancellation fee and/or schedule a refund.
        {cancellationEntitlement && (
          <div className="mt-4 flex flex-row items-center space-x-2 text-supplementary-violet-500">
            <i className="icon icon-stars" />
            <span>{cancellationEntitlement.label}</span>
          </div>
        )}
      </div>
    )
  }

  const renderRemoveItemsCheckbox = () => {
    const unpaidOrFullyRefunded = customerPaymentNetAmount === 0
    const unsettledRefundWouldZeroOutAllPayments =
      hasUnsettledRefundInvoice &&
      unsettledRefundTotalDue === customerPaymentNetAmount
    const noBalanceToRefund =
      unpaidOrFullyRefunded || unsettledRefundWouldZeroOutAllPayments

    const removeItemsCheckboxText = noBalanceToRefund
      ? "Remove unpaid invoices"
      : "Schedule a refund and remove unpaid invoices"

    return (
      <div>
        <Form.Checkbox
          {...register("isRemovingProducts")}
          label={removeItemsCheckboxText}
          disabled={cannotIssueRefund}
        />
      </div>
    )
  }

  const renderNotRemovingItemsHelpText = () => {
    if (isRemoving || cannotIssueRefund) {
      return null
    }
    return (
      <div className="flex flex-row items-start space-x-2">
        <i className="icon icon-info-circle text-lg text-yellow-700" />
        <span>{NOT_REMOVING_ITEMS_TEXT}</span>
      </div>
    )
  }

  const renderPaymentAndRefundAmountsDisplay = () => {
    const customerPaidDisplay = formatCurrencyFromMinorUnits({
      amount: customerPaymentNetAmount,
      currency: presentmentCurrency,
    })
    const effectiveRefundTotal = calculateEffectiveRefundTotal()
    const chargingFeeOnUnpaidTransient =
      isTransientWithNoPayments && cancellationFeeProductId
    const zeroRefundBeforeFee =
      effectiveRefundTotal === 0 && !cancellationFeeProductId

    const refundTotalDisplay =
      zeroRefundBeforeFee || chargingFeeOnUnpaidTransient
        ? "-"
        : formatCurrencyFromMinorUnits({
            amount: effectiveRefundTotal,
            currency: presentmentCurrency,
          })

    return (
      <ReloadableWidget
        isLoading={isLoadingInvoices}
        isError={isErrorInvoices}
        showOverlay
      >
        <div
          className="flex flex-row justify-around gap-8 px-12"
          data-testid="payment-and-refund-display"
        >
          <PricePanel
            title="Customer Paid"
            value={
              <span className="text-2xl font-bold">{customerPaidDisplay}</span>
            }
          />
          <PricePanel
            title="Refund Total"
            value={
              <span
                className={classNames("text-2xl font-bold", {
                  "text-red-700":
                    !chargingFeeOnUnpaidTransient && effectiveRefundTotal < 0,
                })}
              >
                {refundTotalDisplay}
              </span>
            }
          />
        </div>
      </ReloadableWidget>
    )
  }

  const renderCancellationFeeProductSelect = () => {
    return (
      <div>
        <div className="flex justify-between">
          <Form.Label htmlFor="cancellation-fee-product">
            Apply a cancellation fee
          </Form.Label>
          <span
            className="text-link font-semibold"
            onClick={handleFeeProductLinkClick}
          >
            {`${cancellationFeeProductId ? "Edit" : "Add"} fee`}
          </span>
        </div>
        <Form.Select
          id="cancellation-fee-product"
          {...register("cancellationFeeProductId")}
          onChange={(event) =>
            handleCancellationFeeProductSelection(event.target.value)
          }
          disabled={!feeIsSupported}
        >
          <option key={""} value={""}>
            None
          </option>
          {cancellationFeeProducts.map((product) => (
            <option key={product.id} value={product.id}>
              {product.name}
            </option>
          ))}
        </Form.Select>
      </div>
    )
  }

  const renderCancellationFeeInputs = () => {
    if (!cancellationFeeProductId) {
      return null
    }

    const selectedProduct = cancellationFeeProducts.find(
      (product) => product.id === cancellationFeeProductId
    )

    return (
      <div className="flex flex-row items-center gap-4">
        <div className="w-1/2">
          <Form.Label htmlFor="cancellation-fee-amount">
            Cancellation fee amount
          </Form.Label>
          <Form.IconTextField
            id="cancellation-fee-amount"
            {...register("cancellationFeeAmount", {
              required: "Cancellation fee amount is required.",
              valueAsNumber: true,
              validate: validateCancellationFeeAmount,
            })}
            icon={currencySymbol}
            position="left"
            inputMode="numeric"
            type="number"
            hasErrors={!!errors.cancellationFeeAmount}
          />
          {errors?.cancellationFeeAmount ? (
            <Form.Error>{errors.cancellationFeeAmount.message}</Form.Error>
          ) : null}
        </div>
        <div className="w-1/2">
          {useNewTaxExperience ? (
            <TaxRateDisplayInput
              combinedTaxRateDisplay={selectedProduct?.combinedTaxRateDisplay}
              taxes={selectedProduct?.taxes}
              label="Cancellation fee tax"
            />
          ) : (
            <>
              <Form.Label htmlFor="cancellation-fee-tax-rate">
                Cancellation fee tax
              </Form.Label>
              <Form.IconTextField
                id="cancellation-fee-tax-rate"
                {...register("cancellationFeeTaxRate", {
                  required: "Cancellation fee tax is required.",
                  valueAsNumber: true,
                  validate: validateCancellationFeeTaxRate,
                })}
                icon="%"
                position="right"
                inputMode="numeric"
                type="number"
                hasErrors={!!errors.cancellationFeeTaxRate}
              />
              {errors?.cancellationFeeTaxRate ? (
                <Form.Error>{errors.cancellationFeeTaxRate.message}</Form.Error>
              ) : null}
            </>
          )}
        </div>
      </div>
    )
  }

  const cannotIssueRefundHelpText = () => {
    if (hasDisputes) return HAS_DISPUTES_TEXT
    if (isMultiplePaymentRequired) return MULTIPLE_PAYMENT_TEXT
    if (hasProcessingInvoice) return PROCESSING_INVOICE_TEXT
  }

  const renderCannotIssueRefundBanner = () => {
    if (!cannotIssueRefund) {
      return null
    }

    return (
      cannotIssueRefundHelpText() && (
        <AlertBanner type="error">{cannotIssueRefundHelpText()}</AlertBanner>
      )
    )
  }

  const renderUnsettledRefundBanner = () => {
    if (!hasUnsettledRefundInvoice) {
      return null
    }

    return (
      <AlertBanner type="error">{UNSETTLED_REFUND_INVOICE_TEXT}</AlertBanner>
    )
  }

  const renderRefundInputs = () => {
    const applyingFeeToUnpaidTransient =
      isTransientWithNoPayments && cancellationFeeProductId
    const showAdditionalRefundInputs =
      !cannotIssueRefund &&
      isRemoving &&
      (refundablePaymentMethods.length === 1 || applyingFeeToUnpaidTransient)

    if (!showAdditionalRefundInputs) {
      return null
    }

    return (
      <div className="flex flex-row gap-4">
        <div className="w-1/2">
          {isTransientWithNoPayments ? (
            <Form.Select.Custom formLabel="Refund options" disabled>
              <option value="schedule_fee">Schedule fee</option>
            </Form.Select.Custom>
          ) : (
            <RefundOptionsDropdown
              name="refundOption"
              control={control}
              disabled
            />
          )}
        </div>
        <div className="w-1/2" data-testid="due-date">
          <DueDateField
            required
            name="dueDate"
            control={control}
            error={errors?.dueDate}
          />
        </div>
      </div>
    )
  }

  const submitDisabled =
    isLoadingCancellationFeeProducts ||
    isLoadingInvoices ||
    isLoadingRefundablePaymentMethods ||
    isLoading ||
    isSuccess

  return (
    <>
      {renderCancelBtn()}
      <Modal
        isOpen={isOpen}
        onClose={onModalClose}
        afterLeave={reset}
        size="mediumFixed"
      >
        <Modal.Header
          title={`Cancel ${contractQuoteId ? "Contract" : "Reservation"}`}
        />
        <Modal.Body>
          <Form autocomplete={false}>{renderBody()}</Form>
        </Modal.Body>
        <Modal.Footer>
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex justify-end space-x-2">
              <Button variant="tertiary" onClick={onModalClose}>
                Back
              </Button>
              <Button
                type="submit"
                variant="danger"
                isLoading={isLoading || isSuccess}
                disabled={submitDisabled}
                onClick={handleSubmit(onSubmit)}
              >
                Cancel {contractQuoteId ? "Contract" : "Reservation"}
              </Button>
            </div>
            {errors?.root?.serverError ? (
              <Form.Error>{errors.root.serverError.message}</Form.Error>
            ) : null}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

CancelContractModal.propTypes = {
  reservationId: PropTypes.number.isRequired,
  billingContainerId: PropTypes.number,
  contractQuoteId: PropTypes.number,
  hasDisputes: PropTypes.bool,
  buttonVariant: PropTypes.string,
  deprecatedButtonTargetId: PropTypes.string,
  cancellationPolicyPath: PropTypes.string,
  cancellationEntitlement: PropTypes.shape({
    outcome: PropTypes.string,
    label: PropTypes.string,
  }),
  useNewTaxExperience: PropTypes.bool,
}

export default CancelContractModal
